import { Selector, createSelector } from '@reduxjs/toolkit';
import { type RootState } from '../../stores/redux';
import type { ChatMessage, ChatRoom } from './chat.types';
import { shallowEqual } from 'react-redux';

const getUnreadMessagesForRoom = (
  room: ChatRoom | undefined,
): ChatMessage[] => {
  if (room === undefined) {
    return [];
  }
  return room.history.filter(
    (msg) => !msg.isOwn && msg.timestamp > room.lastReadTimestamp,
  );
};

export const _getUnreadMessageCountForRoom = (
  room: ChatRoom | undefined,
): number => {
  return getUnreadMessagesForRoom(room).length;
};

export const _getUnreadMessageCountTotal = (
  rooms: Record<string, ChatRoom>,
): number =>
  Object.values(rooms).reduce(
    (acc, room) => acc + _getUnreadMessageCountForRoom(room),
    0,
  );

export const _getUnreadMessageCountMap = (
  rooms: Record<string, ChatRoom>,
): Record<string, number> => {
  const acc: Record<string, number> = {};
  for (const [roomId, room] of Object.entries(rooms)) {
    acc[roomId] = _getUnreadMessageCountForRoom(room);
  }
  return acc;
};

export const _getUnreadMessages = (
  rooms: Record<string, ChatRoom>,
): ChatMessage[] => {
  let chatMessage: ChatMessage[] = [];
  for (const room of Object.values(rooms)) {
    chatMessage = [...chatMessage, ...getUnreadMessagesForRoom(room)];
  }
  return chatMessage;
};

export const _getIsTyping = (room: ChatRoom | undefined): boolean => {
  if (room === undefined) {
    return false;
  }
  return room.isTyping;
};

export const _getTotalReceivedCount = (
  rooms: Record<string, ChatRoom>,
): number =>
  Object.values(rooms).reduce(
    (acc, room) => acc + room.history.filter((msg) => !msg.isOwn).length,
    0,
  );

export const _getPendingMessage = (room: ChatRoom | undefined): string => {
  if (room === undefined) {
    return '';
  }
  return room.pendingMessage;
};

/* istanbul ignore next */
const getState = (state: RootState): RootState => state;
/* istanbul ignore next */
const getChatState = createSelector(getState, (state: RootState) => state.chat);

/* istanbul ignore next */
const getChatRooms = createSelector(getChatState, (state) => state.rooms);
/* istanbul ignore next */
const getChatRoomById = createSelector(
  getChatRooms,
  (state: RootState, userId: string) => userId,
  (rooms, userId) => rooms[userId],
);

/* istanbul ignore next */
export const getChatUserHistory: Selector<
  RootState,
  ChatMessage[] | undefined,
  [string]
> = createSelector(getChatRoomById, (room) => room?.history);

/* istanbul ignore next */
export const getTotalReceivedCount: Selector<
  RootState,
  number,
  [Record<string, ChatRoom>]
> = createSelector(getChatRooms, (rooms) => _getTotalReceivedCount(rooms));

/* istanbul ignore next */
export const getUnreadMessageCountForUser: Selector<
  RootState,
  number,
  [string]
> = createSelector(getChatRoomById, (room) =>
  _getUnreadMessageCountForRoom(room),
);

/* istanbul ignore next */
export const getUnreadMessageCountTotal: Selector<RootState, number> =
  createSelector(getChatRooms, (rooms) => _getUnreadMessageCountTotal(rooms));

/* istanbul ignore next */
export const getUnreadMessages: Selector<RootState, ChatMessage[]> =
  createSelector(getChatRooms, (rooms) => _getUnreadMessages(rooms), {
    memoizeOptions: {
      resultEqualityCheck: shallowEqual,
    },
  });

/* istanbul ignore next */
export const getUnreadMessageCountMap: Selector<
  RootState,
  Record<string, number>
> = createSelector(getChatRooms, (rooms) => _getUnreadMessageCountMap(rooms));

/* istanbul ignore next */
export const getIsTyping: Selector<RootState, boolean, [string]> =
  createSelector(getChatRoomById, (room) => _getIsTyping(room));

export const getPendingMessage: Selector<RootState, string, [string]> =
  createSelector(getChatRoomById, (room) => _getPendingMessage(room));
